import { convertDateToYYYYMMDDFormat, STATUSPAGE } from '@repo/ui';
import {
  GetAllStudentsSelectOptionsUseCaseInterface,
  GetSuspectedManipulationCountUseCaseInterface,
  GetSuspectedManipulationUseCaseInterface,
  GetSuspectedStudentsRankingUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { loadStudentsSelectOptions } from 'presentation/config/store/reports/handle-reports-analytical-activities-by-sturendt';
import {
  clearSuspectedManipulationCount,
  clearSuspectedManipulationLocation,
  clearSuspectedStudentsRanking,
  saveSuspectedManipulationCount,
  saveSuspectedManipulationLocation,
  saveSuspectedStudentsRanking,
} from 'presentation/config/store/reports/suspected-manipulation-location-slice';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { downloadReport } from '../common/helpers/download-report';

type ValuesToSubmit = {
  organizationHashId?: string;
  courseHashId?: string;
  semesterId?: string;
  curricularUnitHashId?: string;
  groupHashIds?: string[];
  starts: string;
  ends: string;
  studentHashId?: string;
};

type ExportValues = ValuesToSubmit & {
  type: 'pdf' | 'excel';
};

type CreateExportHandlerParams = {
  exportFn: (params: ExportValues) => Promise<{ url: string }>;
  filenamePrefix: string;
  setLoading: React.Dispatch<React.SetStateAction<STATUSPAGE>>;
};

export const useSuspectedLocationManipulation = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<ValuesToSubmit | null>(
    null,
  );
  const [loadingGetAll, setLoadingGetAll] = useState<STATUSPAGE>(
    STATUSPAGE.INITIAL,
  );
  const [loadingActivitiesPdf, setLoadingActivitiesPdf] = useState<STATUSPAGE>(
    STATUSPAGE.INITIAL,
  );
  const [loadingActivitiesExcel, setLoadingActivitiesExcel] =
    useState<STATUSPAGE>(STATUSPAGE.INITIAL);
  const [loadingStudentsPdf, setLoadingStudentsPdf] = useState<STATUSPAGE>(
    STATUSPAGE.INITIAL,
  );
  const [loadingStudentsExcel, setLoadingStudentsExcel] = useState<STATUSPAGE>(
    STATUSPAGE.INITIAL,
  );

  function getFirstDayOfMonth() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  function getCurrentDate() {
    return new Date();
  }

  const dispatch = useDispatch();
  const { reportService, studentService } = useService();

  const { studentsSelectOptions } = useAppSelector(
    (state) => state.reportsAnalyticalActivities,
  );
  const {
    suspectedManipulation,
    suspectedManipulationCount,
    suspectedStudentsRanking,
    suspectedManipulationTotal,
    suspectedStudentsRankingTotal,
  } = useAppSelector((state) => state.suspectedManipulationLocation);

  const requestGetAllStudentsSelectOptions = useCallback(
    async (params?: GetAllStudentsSelectOptionsUseCaseInterface.Params) => {
      try {
        const res = await studentService.getAllStudentsSelectOptions(params);
        dispatch(loadStudentsSelectOptions(res));
      } catch (error) {
        console.log(error);
      }
    },
    [studentService],
  );

  const requestGetSuspectedManipulation = useCallback(
    async (params: GetSuspectedManipulationUseCaseInterface.Params) => {
      try {
        const res = await reportService.getSuspectedManipulation(params);
        dispatch(
          saveSuspectedManipulationLocation({
            data: res.data,
            total: res.total,
          }),
        );
      } catch (err) {
        dispatch(clearSuspectedManipulationLocation());
        notifyError(err.message);
      }
    },
    [reportService],
  );

  const requestGetSuspectedManipulationCount = useCallback(
    async (params: GetSuspectedManipulationCountUseCaseInterface.Params) => {
      try {
        const res = await reportService.getSuspectedManipulationCount(params);
        dispatch(saveSuspectedManipulationCount(res));
      } catch (err) {
        dispatch(clearSuspectedManipulationCount());
        notifyError(err.message);
      }
    },
    [reportService],
  );

  const requestGetSuspectedStudentsRanking = useCallback(
    async (params: GetSuspectedStudentsRankingUseCaseInterface.Params) => {
      try {
        const res = await reportService.getSuspectedStudentsRanking(params);
        dispatch(
          saveSuspectedStudentsRanking({ data: res.data, total: res.total }),
        );
      } catch (err) {
        dispatch(clearSuspectedStudentsRanking());
        notifyError(err.message);
      }
    },
    [reportService],
  );

  const createExportHandler =
    ({ exportFn, filenamePrefix, setLoading }: CreateExportHandlerParams) =>
    async (exportParams: ExportValues) => {
      setLoading(STATUSPAGE.LOADING);

      try {
        const res = await exportFn(exportParams);

        downloadReport(res.url, {
          filename: `${filenamePrefix}_${exportParams.type}`,
          type: exportParams.type,
        });
      } catch (err) {
        notifyError(err.message);
      } finally {
        setLoading(STATUSPAGE.DONE);
      }
    };

  const handleExportSuspectedPdf = useCallback(
    createExportHandler({
      exportFn: (params) => reportService.exportSuspectedManipulation(params),
      filenamePrefix: 'atividades-manipulacao',
      setLoading: setLoadingActivitiesPdf,
    }),
    [reportService],
  );

  const handleExportSuspectedExcel = useCallback(
    createExportHandler({
      exportFn: (params) => reportService.exportSuspectedManipulation(params),
      filenamePrefix: 'atividades-manipulacao',
      setLoading: setLoadingActivitiesExcel,
    }),
    [reportService],
  );

  const handleExportSuspectedStudentsPdf = useCallback(
    createExportHandler({
      exportFn: (params) =>
        reportService.exportSuspectedStudentsRanking(params),
      filenamePrefix: 'ranking-manipulacao',
      setLoading: setLoadingStudentsPdf,
    }),
    [reportService],
  );

  const handleExportSuspectedStudentsExcel = useCallback(
    createExportHandler({
      exportFn: (params) =>
        reportService.exportSuspectedStudentsRanking(params),
      filenamePrefix: 'ranking-manipulacao',
      setLoading: setLoadingStudentsExcel,
    }),
    [reportService],
  );

  const handleSubmit = useCallback(
    async (params: ValuesToSubmit) => {
      setLoadingGetAll(STATUSPAGE.LOADING);

      try {
        await Promise.allSettled([
          requestGetSuspectedManipulation(params),
          requestGetSuspectedManipulationCount(params),
          requestGetSuspectedStudentsRanking(params),
        ]);
      } finally {
        setLoadingGetAll(STATUSPAGE.DONE);
      }
    },
    [
      requestGetSuspectedManipulation,
      requestGetSuspectedManipulationCount,
      requestGetSuspectedStudentsRanking,
    ],
  );

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: null,
      course: null,
      unit: null,
      semester: null,
      groups: [],
      ends: convertDateToYYYYMMDDFormat(getCurrentDate()),
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      student: null,
    },

    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Campo é obrigatório!'),
      ends: Yup.date().required('Campo é obrigatório!'),
      student: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
    }),

    onSubmit: (values) => {
      const valuesToSubmit: ValuesToSubmit = {
        organizationHashId: values.entity?.value,
        courseHashId: values.course?.value,
        semesterId: values.semester?.value,
        curricularUnitHashId: values.unit?.value,
        groupHashIds: values.groups.map((group) => group.value),
        starts: values.starts,
        ends: values.ends,
        studentHashId: values.student?.value,
      };

      setHasSearched(true);
      setAppliedFilters(valuesToSubmit);
      handleSubmit(valuesToSubmit);
    },
  });

  useEffect(() => {
    requestGetAllStudentsSelectOptions({
      course: validation?.values?.course?.value,
      groups: validation?.values?.groups?.map((group) => group?.value),
      entity: validation?.values?.entity?.value,
      curricularUnit: validation?.values?.unit?.value,
      semester: validation?.values?.semester?.value,
    });
  }, [
    validation?.values?.unit,
    validation?.values?.course,
    validation?.values?.groups,
    validation?.values?.entity,
    validation?.values?.semester?.value,
  ]);

  return {
    validation,
    hasSearched,
    appliedFilters,
    isLoadingSubmit: loadingGetAll === STATUSPAGE.LOADING,
    studentsSelectOptions,
    exportActivitiesPdf: {
      request: handleExportSuspectedPdf,
      loading: loadingActivitiesPdf === STATUSPAGE.LOADING,
    },
    exportActivitiesExcel: {
      request: handleExportSuspectedExcel,
      loading: loadingActivitiesExcel === STATUSPAGE.LOADING,
    },
    exportRankingPdf: {
      request: handleExportSuspectedStudentsPdf,
      loading: loadingStudentsPdf === STATUSPAGE.LOADING,
    },
    exportRankingExcel: {
      request: handleExportSuspectedStudentsExcel,
      loading: loadingStudentsExcel === STATUSPAGE.LOADING,
    },
    data: {
      suspectedManipulation,
      suspectedManipulationCount,
      suspectedStudentsRanking,
      suspectedManipulationTotal,
      suspectedStudentsRankingTotal,
    },
  };
};
